import { useState, Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Sidebar } from '@/components/layout/Sidebar';
import { RightSidebar } from '@/components/layout/RightSidebar';
import { getRoomByPath } from '@/constants/rooms';
import { useTheme } from '@/hooks/useTheme';
import { useBadgeEngine } from '@/hooks/useBadgeEngine';
import { pageTransition, useReducedMotion } from '@/lib/animations';
import { getThemedAccent } from '@/styles/themes/presets';
import { trackEvent } from '@/lib/analytics';
import './AppShell.css';

/**
 * AppShell — main app layout with sidebar + content area.
 * Sets the room accent color as a CSS variable on the main content.
 */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const room = getRoomByPath(location.pathname);
  const reducedMotion = useReducedMotion();
  const { activeTheme } = useTheme(); // Apply theme and expose active theme metadata
  useBadgeEngine();

  const roomAccent = getThemedAccent(room?.accentColor ?? '#8B5CF6', activeTheme);

  const lastRoom = useRef<string | null>(null);
  useEffect(() => {
    const path = location.pathname;
    let roomId = room?.id ?? 'dashboard';
    if (path.startsWith('/settings')) roomId = 'settings';
    if (path.startsWith('/admin')) roomId = 'admin';
    if (lastRoom.current === roomId) return;
    lastRoom.current = roomId;
    trackEvent('room_visit', { room: roomId });
  }, [location.pathname, room?.id]);

  return (
    <div className="app-shell" data-section={room?.id ?? 'dashboard'}>
      <a className="skip-nav-link" href="#main-content">
        Skip to main content
      </a>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      <main
        id="main-content"
        className="app-content"
        style={
          {
            '--room-accent': roomAccent,
            '--room-hue': room?.accentHue ?? 270,
          } as React.CSSProperties
        }
      >
        <Suspense fallback={<PageLoader />}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageTransition(reducedMotion)}
              initial="initial"
              animate="animate"
              exit="exit"
              className="app-content-page"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </main>
      <RightSidebar />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="page-loader">
      <div className="page-loader-spinner" />
    </div>
  );
}
